import { useState } from "react";
import { WorkPatternRow } from "../../../generated/core-api";

export interface WorkPatternImportWarning {
  /** 1-indexed CSV row number (row 1 = header, so data rows start at 2). */
  rowNumber: number;
  workPatternName: string;
  /** Index of this pattern in the parsed `data` array. */
  patternIndex: number;
  message: string;
}

/** A CSV row that failed client-side parse validation and has no `WorkPatternRow`. */
export interface WorkPatternImportParseFailedRow {
  /** 1-indexed CSV row number (row 1 = header). */
  csvRowNumber: number;
  workPatternName: string | null;
  error: string;
}

export interface WorkPatternImportCsvParseResult {
  success: boolean;
  data?: WorkPatternRow[];
  /** 1-indexed CSV row numbers aligned with `data` (row 2 = first data row after header). */
  patternCsvRowNumbers?: number[];
  errors?: string[];
  /** Per-row warnings (decimal rounding, StandardHours/StandardDays mismatches). Does not affect `success`. */
  warnings?: WorkPatternImportWarning[];
  /** Rows that failed parse validation — shown in the mixed validation report. */
  failedRows?: WorkPatternImportParseFailedRow[];
  /** Raw CSV text as read from the file — retained so callers can build an errors CSV. */
  rawText?: string;
  /**
   * Number of Day columns found in the CSV header (e.g. 5 when Day1..Day5 are present).
   * Retained so recompute-after-edit can re-emit or clear the CycleDays/header mismatch
   * error (#474) when the user lowers CycleDays in the Edit modal.
   */
  dayColumnCountFromHeader?: number;
}

/**
 * Columns required for the CSV. Matching is done by normalising both
 * this list and incoming headers (lowercase + strip separators), so
 * `WorkPatternName`, `work_pattern_name`, and `WORKPATTERNNAME` all resolve.
 */
const SHARED_REQUIRED_COLUMNS = [
  "WorkPatternName",
  "CycleDays",
  "StartDate",
  "PayPublicHolidays",
  "StandardHours",
  "StandardDays",
] as const;

/**
 * The scope-code column — present when supplied, optional. When absent or
 * left blank, `workPatternCode` is passed through as `null` (not defaulted
 * to `workPatternName`; see Pacific.Agentic.Api#1016).
 */
const SCOPE_CODE_COLUMN = "WorkPatternCode";

/**
 * Maximum length for a non-blank WorkPatternCode value. Matches the Pydantic
 * `max_length=64` on `work_pattern_code` in the Pacific API model. A value
 * over this limit causes a hard 422 at invoke time (aborts the whole batch),
 * so the Web must enforce this ceiling. Exported so the Edit in UI modal can
 * import the same constant rather than duplicating the number.
 */
export const MAX_WORK_PATTERN_CODE_LENGTH = 64;

/** Accepted aliases for the pattern-name column (`WorkPatternName` canonical). */
const PATTERN_NAME_ALIASES = ["WorkPatternName", "PatternName"] as const;

export interface HeaderIndices {
  /** Index of the WorkPatternName column → maps to `workPatternName`. */
  WorkPatternName: number;
  /** Index of the WorkPatternCode column → maps to `workPatternCode`. */
  ScopeCode: number;
  CycleDays: number;
  StartDate: number;
  PayPublicHolidays: number;
  StandardHours: number;
  StandardDays: number;
  /** Indices of Day1..DayN columns, sorted by day number. */
  day_indices: number[];
}

/**
 * Split a single CSV line into fields, honouring double-quoted values so that
 * embedded commas (and escaped `""` quotes) inside a quoted field do not
 * corrupt column alignment. This replaces a naive `split(",")`.
 *
 * Quoting rules (RFC 4180 subset, single-line):
 * - A field may be wrapped in double quotes.
 * - Inside a quoted field, `""` is a literal double quote.
 * - Commas inside a quoted field are part of the value.
 *
 * Multi-line quoted fields (embedded newlines) are not supported — the parser
 * still splits the file into lines first.
 */
function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++; // skip the escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      fields.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  fields.push(current);
  return fields;
}

/**
 * Normalise a header token for matching: lowercase + strip underscores/hyphens.
 * E.g. "CompanyCode" → "companycode", "company_code" → "companycode",
 *      "Day1" → "day1", "day_hours" → "dayhours".
 */
function normaliseHeader(h: string): string {
  return h.toLowerCase().replace(/[_-]/g, "");
}

function validateHeaders(
  normalisedHeaders: string[]
): {
  isValid: boolean;
  errors: string[];
  dayIndices: number[];
} {
  const errors: string[] = [];
  const missing: string[] = [];

  // Forbidden columns — must be checked before missing-column errors so schema
  // violations surface first (NFR-REL-2 validation order: schema before data).
  if (normalisedHeaders.includes(normaliseHeader("CompanyCode"))) {
    errors.push(
      "CompanyCode column must not be present — company scope is entered in the UI, not the CSV"
    );
  }
  if (normalisedHeaders.includes(normaliseHeader("EmployeeCode"))) {
    errors.push(
      "EmployeeCode column is not supported — this import creates Employee Group patterns only; use WorkPatternCode"
    );
  }
  if (errors.length > 0) {
    return { isValid: false, errors, dayIndices: [] };
  }

  const expectedColumns = [
    "WorkPatternName",
    ...SHARED_REQUIRED_COLUMNS.filter((c) => c !== "WorkPatternName"),
  ];

  const hasPatternName = PATTERN_NAME_ALIASES.some((alias) =>
    normalisedHeaders.includes(normaliseHeader(alias))
  );
  if (!hasPatternName) missing.push("WorkPatternName");

  for (const col of SHARED_REQUIRED_COLUMNS) {
    if (col === "WorkPatternName") continue;
    if (!normalisedHeaders.includes(normaliseHeader(col))) {
      missing.push(col);
    }
  }

  // Detect day columns: either Day1..DayN pattern OR legacy repeated "dayhours"
  const dayPattern = /^day(\d+)$/;
  const dayColEntries: { index: number; dayNum: number }[] = [];

  for (let i = 0; i < normalisedHeaders.length; i++) {
    const match = normalisedHeaders[i].match(dayPattern);
    if (match) {
      dayColEntries.push({ index: i, dayNum: parseInt(match[1], 10) });
    }
  }

  // Legacy fallback: if no Day1..DayN found, look for repeated "dayhours"
  if (dayColEntries.length === 0) {
    for (let i = 0; i < normalisedHeaders.length; i++) {
      if (normalisedHeaders[i] === "dayhours") {
        dayColEntries.push({ index: i, dayNum: dayColEntries.length + 1 });
      }
    }
  }

  if (missing.length > 0) {
    errors.push(
      `Missing: ${missing.join(", ")}. CSV must contain columns: ${expectedColumns.join(", ")}, Day1..DayN`
    );
    return { isValid: false, errors, dayIndices: [] };
  }

  if (dayColEntries.length === 0) {
    errors.push(
      "CSV must contain day columns (Day1, Day2, ..., DayN) for work pattern hours"
    );
    return { isValid: false, errors, dayIndices: [] };
  }

  // Sort by day number and return indices in order
  dayColEntries.sort((a, b) => a.dayNum - b.dayNum);

  // Christmas maps Day{n} by property name — headers must be Day1, Day2, … with no gaps.
  for (let i = 0; i < dayColEntries.length; i++) {
    const expectedDayNum = i + 1;
    if (dayColEntries[i].dayNum !== expectedDayNum) {
      errors.push(
        `Day columns must be contiguous from Day1 (expected Day${expectedDayNum}, found Day${dayColEntries[i].dayNum}).`
      );
      return { isValid: false, errors, dayIndices: [] };
    }
  }

  const dayIndices = dayColEntries.map((e) => e.index);

  return { isValid: true, errors: [], dayIndices };
}

const VALID_DECIMAL_RE = /^-?\d+(\.\d+)?$/;
const VALID_INTEGER_RE = /^-?\d+$/;
const MAX_DECIMAL_PLACES = 8;

function countDecimalPlaces(raw: string): number {
  const dotIndex = raw.indexOf(".");
  if (dotIndex === -1) {
    return 0;
  }
  return raw.length - dotIndex - 1;
}

function roundToMaxDecimalPlaces(value: number): number {
  return Math.round(value * 10 ** MAX_DECIMAL_PLACES) / 10 ** MAX_DECIMAL_PLACES;
}

function formatRoundedDecimal(value: number): string {
  return value.toFixed(MAX_DECIMAL_PLACES).replace(/\.?0+$/, "");
}

function validateDecimalField(
  raw: string,
  fieldLabel: string
): { value?: number; error?: string; roundingWarning?: string } {
  if (raw.includes(",")) {
    return {
      error: `${fieldLabel} contains a comma — use a decimal point, e.g. 7.5`,
    };
  }
  if (!VALID_DECIMAL_RE.test(raw)) {
    return {
      error: `${fieldLabel} contains invalid number '${raw}'`,
    };
  }

  const value = parseFloat(raw);
  if (countDecimalPlaces(raw) > MAX_DECIMAL_PLACES) {
    const rounded = roundToMaxDecimalPlaces(value);
    return {
      value: rounded,
      roundingWarning: `${fieldLabel} value '${raw}' had more than ${MAX_DECIMAL_PLACES} decimal places and was rounded to ${formatRoundedDecimal(rounded)}`,
    };
  }

  return { value };
}

function validateNumericString(
  raw: string,
  fieldLabel: string,
  rowNumber: number,
  integerOnly: boolean
): { value?: number; error?: string; roundingWarning?: string } {
  if (raw.includes(",")) {
    return {
      error: `Row ${rowNumber}: ${fieldLabel} contains a comma — use a decimal point, e.g. 7.5`,
    };
  }
  const re = integerOnly ? VALID_INTEGER_RE : VALID_DECIMAL_RE;
  if (!re.test(raw)) {
    return {
      error: `Row ${rowNumber}: ${fieldLabel} contains invalid number '${raw}'`,
    };
  }
  if (integerOnly) {
    return { value: parseInt(raw, 10) };
  }

  const decimalResult = validateDecimalField(raw, fieldLabel);
  if (decimalResult.error) {
    return { error: `Row ${rowNumber}: ${decimalResult.error}` };
  }
  return decimalResult;
}

const MAX_IMPORT_CYCLE_DAYS = 365;

/** Client parse error for duplicate names within one CSV batch (AC-06). */
export function isDuplicateWorkPatternNameParseError(error: string): boolean {
  return error.includes("Duplicate workPatternName");
}

/** Re-run in-batch duplicate-name detection after Edit in UI renames a pattern (#435). */
export function recomputeDuplicateWorkPatternNameParseErrors(
  patterns: WorkPatternRow[],
  patternCsvRowNumbers: number[]
): string[] {
  const errors: string[] = [];
  const seenNames = new Map<string, number>();

  patterns.forEach((pattern, index) => {
    const rowNumber = patternCsvRowNumbers[index] ?? index + 2;
    const rawName = pattern.workPatternName.trim();
    if (!rawName) {
      return;
    }
    const normalisedName = rawName.toLowerCase().replace(/\s+/g, " ");
    const firstSeenRow = seenNames.get(normalisedName);
    if (firstSeenRow !== undefined) {
      errors.push(
        `Row ${rowNumber}: Duplicate workPatternName '${rawName}': first seen at row ${firstSeenRow}`
      );
    } else {
      seenNames.set(normalisedName, rowNumber);
    }
  });

  return errors;
}

/** Returns true when the error message is an out-of-range day-hour error (#472). */
function isOutOfRangeDayHoursParseError(error: string): boolean {
  return error.includes("is out of range — must be between 0 and 24");
}

/** Returns true when the error message is a CycleDays/header Day-column-count mismatch (#474). */
function isHeaderDayColumnMismatchParseError(error: string): boolean {
  return error.includes("Day column(s) but CycleDays");
}

/** Client parse errors that Edit in UI can fix on a row that still has `WorkPatternRow` data (#435). */
export function isEditInUiRepairableParseError(error: string): boolean {
  return (
    error.includes("WorkPatternName is required") ||
    error.includes("WorkPatternName must be 255 characters or fewer") ||
    error.includes(
      `${SCOPE_CODE_COLUMN} must be ${MAX_WORK_PATTERN_CODE_LENGTH} characters or fewer`
    ) ||
    error.includes("PayPublicHolidays must be") ||
    error.includes("CycleDays must be") ||
    error.includes("CycleDays contains invalid number") ||
    isOutOfRangeDayHoursParseError(error) ||
    isHeaderDayColumnMismatchParseError(error)
  );
}

/**
 * Parse raw day-hour string values into a number array for use in an editable
 * draft — like `validateDayHoursFromStrings` but tolerates out-of-range values
 * (< 0 or > 24) so the Edit modal can prefill and fix the bad cell (#472).
 *
 * Returns `undefined` when the data is structurally unusable as a draft:
 * any blank or non-numeric value, empty input, or an all-zero result
 * (all-zero is kept as a non-repairable failed row, preserving existing #435 behaviour).
 */
export function parseDayHoursForEditableDraft(rawValues: string[]): number[] | undefined {
  if (rawValues.length === 0) return undefined;
  const hours: number[] = [];
  for (const rawValue of rawValues) {
    const raw = rawValue?.trim() ?? "";
    if (raw === "") return undefined; // blank cell — not safe for a draft
    if (!VALID_DECIMAL_RE.test(raw)) return undefined; // non-numeric
    const parsed = parseFloat(raw);
    if (!isFinite(parsed)) return undefined;
    hours.push(parsed);
  }
  // All-zero cycle is not repairable via Edit in UI — keep as a failed row.
  if (hours.every((h) => h === 0)) return undefined;
  return hours;
}

const PARSE_ERROR_ROW_LINE = /^Row (\d+):\s*(.+)$/;

function parseErrorLineRowNumber(line: string): number | undefined {
  const match = line.match(PARSE_ERROR_ROW_LINE);
  return match ? Number(match[1]) : undefined;
}

/**
 * Re-run repairable client parse checks for one parsed pattern after Edit in UI.
 * Returns full `Row N: …` lines aligned with initial CSV parse wording.
 *
 * @param dayColumnCountFromHeader - number of Day columns in the CSV header.
 *   When provided, re-emits the CycleDays/header mismatch error (#474) if the
 *   pattern's cycleDays still exceeds the column count, or clears it when fixed.
 */
export function computeRepairableParseErrorsForPattern(
  pattern: WorkPatternRow,
  rowNumber: number,
  dayColumnCountFromHeader?: number
): string[] {
  const errors: string[] = [];

  const workPatternName = pattern.workPatternName.trim();
  if (!workPatternName) {
    errors.push(`Row ${rowNumber}: WorkPatternName is required`);
  } else if (workPatternName.length > 255) {
    errors.push(
      `Row ${rowNumber}: WorkPatternName must be 255 characters or fewer`
    );
  }

  const workPatternCode = pattern.workPatternCode;
  if (
    typeof workPatternCode === "string" &&
    workPatternCode.length > MAX_WORK_PATTERN_CODE_LENGTH
  ) {
    errors.push(
      `Row ${rowNumber}: ${SCOPE_CODE_COLUMN} must be ${MAX_WORK_PATTERN_CODE_LENGTH} characters or fewer`
    );
  }

  if (pattern.payPublicHolidays !== "Yes" && pattern.payPublicHolidays !== "No") {
    errors.push(
      `Row ${rowNumber}: PayPublicHolidays must be 'Yes' or 'No', got '${pattern.payPublicHolidays}'`
    );
  }

  const cycleDaysRaw = String(pattern.cycleDays);
  const cycleDaysResult = validateNumericString(
    cycleDaysRaw,
    "CycleDays",
    rowNumber,
    true
  );
  const cycleDays = cycleDaysResult.value ?? NaN;
  if (cycleDaysResult.error || cycleDays <= 0) {
    errors.push(
      cycleDaysResult.error ??
        `Row ${rowNumber}: CycleDays must be a whole number between 1 and 365, got '${cycleDaysRaw}'`
    );
  } else if (cycleDays > MAX_IMPORT_CYCLE_DAYS) {
    errors.push(
      `Row ${rowNumber}: CycleDays must be ${MAX_IMPORT_CYCLE_DAYS} or fewer, got '${cycleDaysRaw}'`
    );
  }

  // #474: re-emit header mismatch when CycleDays still exceeds the file's Day column count.
  // Skip when CycleDays is already invalid — range error is the actionable fix (#486).
  if (
    dayColumnCountFromHeader !== undefined &&
    !Number.isNaN(cycleDays) &&
    cycleDays >= 1 &&
    cycleDays <= MAX_IMPORT_CYCLE_DAYS &&
    cycleDays > dayColumnCountFromHeader
  ) {
    errors.push(
      `Row ${rowNumber}: header has ${dayColumnCountFromHeader} Day column(s) but CycleDays is ${cycleDays}`
    );
  }

  // #472: re-validate per-day hour values against the 0–24 range.
  if (pattern.dayHours.length > 0) {
    const dayHoursResult = validateDayHoursFromStrings(
      pattern.dayHours.map(String),
      pattern.dayHours.length,
      { rowNumber }
    );
    if (dayHoursResult.error) {
      errors.push(dayHoursResult.error);
    }
  }

  return errors;
}

export interface RecomputeParseErrorsAfterPatternEditInput {
  prev: string[];
  updated: WorkPatternRow[];
  editedIndex: number;
  patternCsvRowNumbers: number[];
  /**
   * Number of Day columns in the CSV header — used to re-emit or clear the
   * CycleDays/header mismatch error (#474) when the user edits CycleDays.
   */
  dayColumnCountFromHeader?: number;
}

/**
 * Refresh `parseErrors` after Edit in UI: strip repairable errors for the edited
 * CSV row, recompute them from the updated pattern, and refresh in-batch
 * duplicate-name errors across the batch.
 */
export function recomputeParseErrorsAfterPatternEdit({
  prev,
  updated,
  editedIndex,
  patternCsvRowNumbers,
  dayColumnCountFromHeader,
}: RecomputeParseErrorsAfterPatternEditInput): string[] {
  const csvRowNumber =
    patternCsvRowNumbers[editedIndex] ?? editedIndex + 2;

  const withoutRepairableForEditedRow = prev.filter((line) => {
    if (isDuplicateWorkPatternNameParseError(line)) {
      return false;
    }
    const rowNumber = parseErrorLineRowNumber(line);
    if (rowNumber !== csvRowNumber) {
      return true;
    }
    return !isEditInUiRepairableParseError(line);
  });

  const repairableForEditedRow = computeRepairableParseErrorsForPattern(
    updated[editedIndex],
    csvRowNumber,
    dayColumnCountFromHeader
  );
  const duplicateErrors = recomputeDuplicateWorkPatternNameParseErrors(
    updated,
    patternCsvRowNumbers
  );

  return [
    ...withoutRepairableForEditedRow,
    ...repairableForEditedRow,
    ...duplicateErrors,
  ];
}

function errorsAreEditInUiRepairable(errors: string[]): boolean {
  return (
    errors.length > 0 && errors.every((error) => isEditInUiRepairableParseError(error))
  );
}

function parseYesNo(value: string, fieldName: string, rowNumber: number): {
  value?: string;
  error?: string;
} {
  const normalised = value.toLowerCase();
  if (normalised === "yes") return { value: "Yes" };
  if (normalised === "no") return { value: "No" };
  return {
    error: `Row ${rowNumber}: ${fieldName} must be 'Yes' or 'No', got '${value}'`,
  };
}


interface ValidateDayHoursFromStringsOptions {
  /** When set, error messages are prefixed with `Row N:` for CSV parsing. */
  rowNumber?: number;
}

function formatDayHoursError(
  rowNumber: number | undefined,
  message: string
): string {
  return rowNumber !== undefined ? `Row ${rowNumber}: ${message}` : message;
}

/** Same rule as ComplexWorkPatternImportDto.ValidateAllCycleDaysSupplied; Web uses "CycleDays" wording. */
function formatMissingDayHoursError(
  cycleDays: number,
  missingDays: number[]
): string {
  const missingDayNames = missingDays.map((day) => `Day${day}`).join(", ");
  const wasOrWere = missingDays.length === 1 ? "was" : "were";
  return (
    `Invalid Data: expected ${cycleDays} CycleDays values, ` +
    `but ${missingDayNames} ${wasOrWere} not supplied.`
  );
}

/** Same rule as ComplexWorkPatternImportDto.ValidateDaysNotBeyondCycleDays (#1018). */
function formatDayBeyondCycleDaysError(dayNum: number, cycleDays: number): string {
  return `Invalid Data: Day${dayNum} and onwards must be empty when CycleDays is ${cycleDays}.`;
}

/**
 * Validate and parse per-day hour strings for a pattern cycle.
 * Shared by CSV parsing and the Edit in UI modal so rules stay aligned.
 */
export function validateDayHoursFromStrings(
  rawValues: string[],
  cycleDays: number,
  options: ValidateDayHoursFromStringsOptions = {}
): { value?: number[]; error?: string; roundingWarnings?: string[] } {
  const { rowNumber } = options;

  if (rawValues.length !== cycleDays) {
    return {
      error: formatDayHoursError(
        rowNumber,
        `Expected ${cycleDays} day hour value(s), got ${rawValues.length}`
      ),
    };
  }

  const missingDays: number[] = [];
  for (let i = 0; i < cycleDays; i++) {
    if ((rawValues[i]?.trim() ?? "") === "") {
      missingDays.push(i + 1);
    }
  }
  if (missingDays.length > 0) {
    return {
      error: formatDayHoursError(
        rowNumber,
        formatMissingDayHoursError(cycleDays, missingDays)
      ),
    };
  }

  const hours: number[] = [];
  const roundingWarnings: string[] = [];
  for (let i = 0; i < cycleDays; i++) {
    const raw = rawValues[i]?.trim() ?? "";
    const fieldLabel = `Day${i + 1}`;
    const parsed =
      rowNumber !== undefined
        ? validateNumericString(raw, fieldLabel, rowNumber, false)
        : validateDecimalField(raw, fieldLabel);
    if (parsed.error) {
      return { error: parsed.error };
    }
    if (parsed.roundingWarning) {
      roundingWarnings.push(parsed.roundingWarning);
    }
    const value = parsed.value!;
    if (value < 0 || value > 24) {
      return {
        error: formatDayHoursError(
          rowNumber,
          `${fieldLabel} value '${raw}' is out of range — must be between 0 and 24`
        ),
      };
    }
    hours.push(value);
  }

  if (hours.every((h) => h === 0)) {
    return {
      error: formatDayHoursError(
        rowNumber,
        "At least one day must have hours greater than 0"
      ),
    };
  }

  return {
    value: hours,
    roundingWarnings: roundingWarnings.length > 0 ? roundingWarnings : undefined,
  };
}

/**
 * Parse day hours from the Day1..DayN columns identified in the header.
 * `dayIndices` maps day 1→N to the column indices in the row (contiguous headers).
 */
function parseDayHours(
  values: string[],
  dayIndices: number[],
  cycleDays: number,
  rowNumber: number
): { value?: number[]; error?: string; roundingWarnings?: string[] } {
  if (dayIndices.length < cycleDays) {
    return {
      error: `Row ${rowNumber}: header has ${dayIndices.length} Day column(s) but CycleDays is ${cycleDays}`,
    };
  }

  // Christmas ValidateDaysNotBeyondCycleDays — reject any populated Day{n} beyond
  // CycleDays before slicing, so excess hours are not silently truncated (#1018).
  for (let i = cycleDays; i < dayIndices.length; i++) {
    const raw = values[dayIndices[i]]?.trim() ?? "";
    if (raw !== "") {
      const dayNum = i + 1;
      return {
        error: `Row ${rowNumber}: ${formatDayBeyondCycleDaysError(dayNum, cycleDays)}`,
      };
    }
  }

  const rawValues = Array.from({ length: cycleDays }, (_, i) =>
    values[dayIndices[i]] ?? ""
  );

  return validateDayHoursFromStrings(rawValues, cycleDays, { rowNumber });
}

function parseRow(
  values: string[],
  indices: HeaderIndices,
  rowNumber: number
): { data?: WorkPatternRow; errors: string[]; warnings: Omit<WorkPatternImportWarning, "patternIndex">[] } {
  const errors: string[] = [];

  const scalarIndices = [
    indices.WorkPatternName,
    indices.CycleDays,
    indices.StartDate,
    indices.PayPublicHolidays,
    indices.StandardHours,
    indices.StandardDays,
    ...(indices.ScopeCode >= 0 ? [indices.ScopeCode] : []),
  ];

  const maxScalarIndex = Math.max(...scalarIndices);

  if (values.length <= maxScalarIndex) {
    errors.push(`Row ${rowNumber}: Not enough columns`);
    return { errors, warnings: [] };
  }

  const workPatternName = values[indices.WorkPatternName]?.trim();
  const scopeCodeRaw =
    indices.ScopeCode >= 0 ? values[indices.ScopeCode]?.trim() ?? "" : "";
  const workPatternCode = scopeCodeRaw === "" ? null : scopeCodeRaw;
  const cycleDaysRaw = values[indices.CycleDays]?.trim();
  const startDate = values[indices.StartDate]?.trim();
  const payPublicHolidaysRaw = values[indices.PayPublicHolidays]?.trim();
  const standardHoursRaw = values[indices.StandardHours]?.trim();
  const standardDaysRaw = values[indices.StandardDays]?.trim();

  if (!workPatternName) {
    errors.push(`Row ${rowNumber}: WorkPatternName is required`);
  } else if (workPatternName.length > 255) {
    errors.push(`Row ${rowNumber}: WorkPatternName must be 255 characters or fewer`);
  }

  if (workPatternCode !== null && workPatternCode.length > MAX_WORK_PATTERN_CODE_LENGTH) {
    errors.push(`Row ${rowNumber}: ${SCOPE_CODE_COLUMN} must be ${MAX_WORK_PATTERN_CODE_LENGTH} characters or fewer`);
  }

  // Validate and normalise StartDate to ISO 8601 (YYYY-MM-DD) for the API.
  // Accepts DD/MM/YYYY (canonical PRD format), DD/MM/YY (2-digit year, treated
  // as 20YY), or YYYY-MM-DD (already ISO).
  let startDateIso: string | undefined;
  if (!startDate) {
    errors.push(`Row ${rowNumber}: StartDate is required`);
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
    startDateIso = startDate;
  } else {
    const dmyFullMatch = startDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    const dmyShortMatch = startDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
    if (dmyFullMatch) {
      const [, d, m, y] = dmyFullMatch;
      startDateIso = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    } else if (dmyShortMatch) {
      const [, d, m, y] = dmyShortMatch;
      startDateIso = `20${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    } else {
      errors.push(
        `Row ${rowNumber}: StartDate must be DD/MM/YYYY, DD/MM/YY, or YYYY-MM-DD, got '${startDate}'`
      );
    }
  }

  // Calendar round-trip check: reject impossible dates like 31/02/2026.
  if (startDateIso) {
    const [yr, mo, dy] = startDateIso.split("-").map(Number);
    const probe = new Date(Date.UTC(yr, mo - 1, dy));
    if (
      probe.getUTCFullYear() !== yr ||
      probe.getUTCMonth() + 1 !== mo ||
      probe.getUTCDate() !== dy
    ) {
      errors.push(
        `Row ${rowNumber}: StartDate '${startDate}' is not a valid calendar date`
      );
      startDateIso = undefined;
    }
  }

  const cycleDaysResult = validateNumericString(
    cycleDaysRaw,
    "CycleDays",
    rowNumber,
    true
  );
  const cycleDays = cycleDaysResult.value ?? NaN;
  if (cycleDaysResult.error || cycleDays <= 0) {
    errors.push(
      cycleDaysResult.error ??
        `Row ${rowNumber}: CycleDays must be a whole number between 1 and 365, got '${cycleDaysRaw}'`
    );
  } else if (cycleDays > 365) {
    errors.push(
      `Row ${rowNumber}: CycleDays must be 365 or fewer, got '${cycleDaysRaw}'`
    );
  }

  const standardHoursResult = validateNumericString(
    standardHoursRaw,
    "StandardHours",
    rowNumber,
    false
  );
  const standardHours = standardHoursResult.value ?? NaN;
  if (!standardHoursRaw || standardHoursResult.error) {
    errors.push(
      standardHoursResult.error ??
        `Row ${rowNumber}: StandardHours must be a number, got '${standardHoursRaw}'`
    );
  }

  const standardDaysResult = validateNumericString(
    standardDaysRaw,
    "StandardDays",
    rowNumber,
    false
  );
  const standardDays = standardDaysResult.value ?? NaN;
  if (!standardDaysRaw || standardDaysResult.error) {
    errors.push(
      standardDaysResult.error ??
        `Row ${rowNumber}: StandardDays must be a number, got '${standardDaysRaw}'`
    );
  }

  const payPublicHolidaysResult = parseYesNo(
    payPublicHolidaysRaw,
    "PayPublicHolidays",
    rowNumber
  );
  if (payPublicHolidaysResult.error) {
    errors.push(payPublicHolidaysResult.error);
  }

  const cycleDaysForDayHours =
    !Number.isNaN(cycleDays) &&
    cycleDays > 0 &&
    cycleDays <= MAX_IMPORT_CYCLE_DAYS
      ? cycleDays
      : Math.min(
          indices.day_indices.length > 0 ? indices.day_indices.length : 7,
          MAX_IMPORT_CYCLE_DAYS
        );

  let dayHoursResult: {
    value?: number[];
    error?: string;
    roundingWarnings?: string[];
  } = {};
  if (cycleDaysForDayHours > 0) {
    dayHoursResult = parseDayHours(
      values,
      indices.day_indices,
      cycleDaysForDayHours,
      rowNumber
    );
    if (dayHoursResult.error) {
      errors.push(dayHoursResult.error);
    }
  }

  if (errors.length > 0) {
    if (
      !errorsAreEditInUiRepairable(errors) ||
      !startDateIso ||
      Number.isNaN(standardHours) ||
      Number.isNaN(standardDays)
    ) {
      return { errors, warnings: [] };
    }

    // When normal day-hours parsing failed, try to recover a draft array so the
    // Edit modal can prefill the bad cell(s).
    let draftDayHours = dayHoursResult.value;
    if (!draftDayHours) {
      const dayHoursError = dayHoursResult.error ?? "";
      if (isOutOfRangeDayHoursParseError(dayHoursError)) {
        // #472: parse all cycleDays columns, tolerate out-of-range values.
        const rawVals = Array.from({ length: cycleDaysForDayHours }, (_, i) =>
          values[indices.day_indices[i]] ?? ""
        );
        draftDayHours = parseDayHoursForEditableDraft(rawVals);
      } else if (isHeaderDayColumnMismatchParseError(dayHoursError)) {
        // #474: parse only the available Day columns so the modal shows the mismatch
        // and the user can lower CycleDays to match the actual column count.
        const rawVals = indices.day_indices.map((idx) => values[idx] ?? "");
        draftDayHours = parseDayHoursForEditableDraft(rawVals);
      }
    }

    if (!draftDayHours) {
      return { errors, warnings: [] };
    }

    // Keep the CSV CycleDays in the editable draft even when out of range (e.g. 400)
    // so Edit in UI prefills the invalid value; dayHours uses draftDayHours above.
    const declaredCycleDays =
      cycleDaysResult.error === undefined && !Number.isNaN(cycleDays)
        ? cycleDays
        : draftDayHours.length;

    const editableDraft: WorkPatternRow = {
      workPatternName: workPatternName ?? "",
      workPatternCode,
      cycleDays: declaredCycleDays,
      startDate: startDateIso,
      payPublicHolidays:
        payPublicHolidaysResult.value ?? payPublicHolidaysRaw?.trim() ?? "",
      standardHours,
      standardDays,
      dayHours: draftDayHours,
    };

    return { data: editableDraft, errors, warnings: [] };
  }

  const pattern: WorkPatternRow = {
    workPatternName: workPatternName!,
    workPatternCode,
    cycleDays,
    startDate: startDateIso!,
    payPublicHolidays: payPublicHolidaysResult.value!,
    standardHours,
    standardDays,
    dayHours: dayHoursResult.value!,
  };

  const decimalRoundingWarnings = buildDecimalRoundingWarnings(
    [
      standardHoursResult.roundingWarning,
      standardDaysResult.roundingWarning,
      ...(dayHoursResult.roundingWarnings ?? []),
    ],
    rowNumber,
    workPatternName!
  );

  const warnings = [
    ...decimalRoundingWarnings,
    ...buildStandardValuesWarnings(pattern, rowNumber, workPatternName!),
  ];

  return { data: pattern, errors: [], warnings };
}

function buildDecimalRoundingWarnings(
  messages: Array<string | undefined>,
  rowNumber: number,
  workPatternName: string
): Omit<WorkPatternImportWarning, "patternIndex">[] {
  return messages
    .filter((message): message is string => message !== undefined)
    .map((message) => ({ rowNumber, workPatternName, message }));
}

/**
 * Returns a row-level warning message when declared StandardHours/StandardDays
 * don't match weekly averages from dayHours (R13 / AC-15–AC-17).
 * Shared by CSV parse and post-edit recompute in the import manager.
 */
export function computeStandardValuesMismatchMessage(
  pattern: Pick<
    WorkPatternRow,
    "cycleDays" | "dayHours" | "standardHours" | "standardDays"
  >
): string | null {
  const weeks = pattern.cycleDays / 7;
  const calculatedHours =
    pattern.dayHours.reduce((sum, h) => sum + h, 0) / weeks;
  const calculatedDays =
    pattern.dayHours.filter((h) => h > 0).length / weeks;

  const mismatchParts: string[] = [];
  if (Math.abs(calculatedHours - pattern.standardHours) > 0.01) {
    mismatchParts.push(
      `StandardHours is ${pattern.standardHours} but the day pattern averages ${Math.round(calculatedHours * 100) / 100} hours/week`
    );
  }
  if (Math.abs(calculatedDays - pattern.standardDays) > 0.01) {
    mismatchParts.push(
      `StandardDays is ${pattern.standardDays} but the day pattern averages ${Math.round(calculatedDays * 100) / 100} days/week`
    );
  }

  return mismatchParts.length > 0 ? mismatchParts.join("; ") : null;
}

function buildStandardValuesWarnings(
  pattern: WorkPatternRow,
  rowNumber: number,
  workPatternName: string
): Omit<WorkPatternImportWarning, "patternIndex">[] {
  const message = computeStandardValuesMismatchMessage(pattern);
  if (!message) {
    return [];
  }
  return [{ rowNumber, workPatternName, message }];
}

/**
 * Resolve `HeaderIndices` from a raw CSV header line without running any batch
 * validation. Returns `null` when the header is unparseable (missing required
 * columns, forbidden columns, or no day columns).
 *
 * Used by the errors-CSV export so it can map edited `WorkPatternRow` values
 * back onto the correct column positions in the original upload.
 */
export function resolveHeaderIndicesFromCsvHeader(
  headerLine: string
): HeaderIndices | null {
  const rawHeaders = splitCsvLine(headerLine).map((h) => h.trim());
  const normalisedHeaders = rawHeaders.map(normaliseHeader);
  const headerValidation = validateHeaders(normalisedHeaders);
  if (!headerValidation.isValid) return null;

  const findCol = (name: string) =>
    normalisedHeaders.indexOf(normaliseHeader(name));
  const findColAny = (names: readonly string[]) => {
    for (const name of names) {
      const index = findCol(name);
      if (index >= 0) return index;
    }
    return -1;
  };

  return {
    WorkPatternName: findColAny(PATTERN_NAME_ALIASES),
    ScopeCode: findCol(SCOPE_CODE_COLUMN),
    CycleDays: findCol("CycleDays"),
    StartDate: findCol("StartDate"),
    PayPublicHolidays: findCol("PayPublicHolidays"),
    StandardHours: findCol("StandardHours"),
    StandardDays: findCol("StandardDays"),
    day_indices: headerValidation.dayIndices,
  };
}

/**
 * Overwrite mapped scalar and day-hour columns in a row's cell array with
 * current values from a `WorkPatternRow`. Columns whose header was absent
 * (`index === -1`) and day columns beyond `dayHours.length` are left unchanged.
 *
 * Used by the errors-CSV export so that in-UI edits (name, code, cycle days,
 * day hours, start date, etc.) appear in the re-uploadable CSV rather than the
 * stale original values.
 *
 * `StartDate` is formatted as `DD/MM/YYYY` — the canonical PRD format that the
 * CSV parser already accepts on re-upload.
 */
export function mergeWorkPatternRowIntoCsvCells(
  originalCells: string[],
  indices: HeaderIndices,
  pattern: WorkPatternRow
): string[] {
  const cells = [...originalCells];

  const setCell = (index: number, value: string) => {
    if (index >= 0 && index < cells.length) {
      cells[index] = value;
    }
  };

  setCell(indices.WorkPatternName, pattern.workPatternName);
  setCell(indices.ScopeCode, pattern.workPatternCode ?? "");
  setCell(indices.CycleDays, String(pattern.cycleDays));
  setCell(indices.PayPublicHolidays, pattern.payPublicHolidays);
  setCell(indices.StandardHours, String(pattern.standardHours));
  setCell(indices.StandardDays, String(pattern.standardDays));

  // StartDate: convert ISO YYYY-MM-DD → DD/MM/YYYY for re-uploadable CSV.
  const isoMatch = pattern.startDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const startDateDisplay = isoMatch
    ? `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`
    : pattern.startDate;
  setCell(indices.StartDate, startDateDisplay);

  // Day columns — only overwrite positions that exist in the pattern's dayHours.
  for (let i = 0; i < indices.day_indices.length; i++) {
    if (i < pattern.dayHours.length) {
      setCell(indices.day_indices[i], String(pattern.dayHours[i]));
    }
    // Columns beyond dayHours.length are left untouched (original upload shape).
  }

  return cells;
}

export function parseCSVText(
  text: string
): WorkPatternImportCsvParseResult {
  const lines = text.trim().split("\n");

  if (lines.length < 2) {
    return {
      success: false,
      errors: ["CSV file must contain a header row and at least one data row"],
    };
  }

  const rawHeaders = splitCsvLine(lines[0]).map((h) => h.trim());
  const normalisedHeaders = rawHeaders.map(normaliseHeader);
  const headerValidation = validateHeaders(normalisedHeaders);

  if (!headerValidation.isValid) {
    return { success: false, errors: headerValidation.errors };
  }

  const findCol = (name: string) =>
    normalisedHeaders.indexOf(normaliseHeader(name));

  const findColAny = (names: readonly string[]) => {
    for (const name of names) {
      const index = findCol(name);
      if (index >= 0) return index;
    }
    return -1;
  };

  const indices: HeaderIndices = {
    WorkPatternName: findColAny(PATTERN_NAME_ALIASES),
    ScopeCode: findCol(SCOPE_CODE_COLUMN),
    CycleDays: findCol("CycleDays"),
    StartDate: findCol("StartDate"),
    PayPublicHolidays: findCol("PayPublicHolidays"),
    StandardHours: findCol("StandardHours"),
    StandardDays: findCol("StandardDays"),
    day_indices: headerValidation.dayIndices,
  };

  const data: WorkPatternRow[] = [];
  const patternCsvRowNumbers: number[] = [];
  const allErrors: string[] = [];
  const allWarnings: WorkPatternImportWarning[] = [];
  const failedRows: WorkPatternImportParseFailedRow[] = [];

  // AC-06: track seen names as we parse so duplicate errors appear inline at
  // the correct CSV row, not as a separate post-processing batch.
  // Maps normalised name → first CSV row number (1-based, header = row 1).
  const seenNames = new Map<string, number>();

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // skip blank lines

    const values = splitCsvLine(line).map((v) => v.trim());
    if (values.every((v) => v === "")) continue; // skip comma-only blank rows

    const rowNumber = i + 1;
    const rowResult = parseRow(values, indices, rowNumber);

    allErrors.push(...rowResult.errors);

    // Duplicate name check — runs on the raw value regardless of whether the
    // row passed other validation, so the error appears alongside any other
    // errors for that row rather than being suppressed on invalid rows.
    const rawName = values[indices.WorkPatternName]?.trim() ?? "";
    if (rawName) {
      const normalisedName = rawName.toLowerCase().replace(/\s+/g, " ");
      const firstSeenRow = seenNames.get(normalisedName);
      if (firstSeenRow !== undefined) {
        allErrors.push(
          `Row ${rowNumber}: Duplicate workPatternName '${rawName}': first seen at row ${firstSeenRow}`
        );
      } else {
        seenNames.set(normalisedName, rowNumber);
      }
    }

    if (rowResult.data) {
      const patternIndex = data.length;
      patternCsvRowNumbers.push(rowNumber);
      data.push(rowResult.data);
      allWarnings.push(
        ...rowResult.warnings.map((w) => ({ ...w, patternIndex }))
      );
    } else if (rowResult.errors.length > 0) {
      failedRows.push({
        csvRowNumber: rowNumber,
        workPatternName: rawName || null,
        error: rowResult.errors.join("; "),
      });
    }
  }

  return {
    success: allErrors.length === 0,
    data,
    patternCsvRowNumbers:
      patternCsvRowNumbers.length > 0 ? patternCsvRowNumbers : undefined,
    errors: allErrors.length > 0 ? allErrors : undefined,
    warnings: allWarnings.length > 0 ? allWarnings : undefined,
    failedRows: failedRows.length > 0 ? failedRows : undefined,
    dayColumnCountFromHeader: indices.day_indices.length,
  };
}

export function useWorkPatternImportCsvParser() {
  const [isProcessing, setIsProcessing] = useState(false);

  const parseFile = async (
    file: File
  ): Promise<WorkPatternImportCsvParseResult> => {
    setIsProcessing(true);

    try {
      const text = await file.text();
      return { ...parseCSVText(text), rawText: text };
    } catch (error) {
      return {
        success: false,
        errors: ["Failed to read file: " + (error instanceof Error ? error.message : String(error))],
      };
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    parseFile,
    isProcessing,
  };
}
